import { AddRounded, Troubleshoot } from '@mui/icons-material'
import { Button, FormControlLabel, Modal, Stack, Switch, Typography } from '@mui/material'
import { useState } from 'react'
import { IContract } from '../../definitions/Contract'
import { IUploadedResult } from '../../definitions/Upload'
import { CSimilarity } from '../../managers/ContractsManager'
import { useAppDispatch, useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import ModalCreateReport from '../contract_tabs/ModalCreateReport'
import TabsHeader, { ITabItem } from '../controls/TabsHeader'
import Tile from '../layout/Tile'
import NewContractModal from '../NewContractModal'
import PermissionsWrapper from '../permissions/PermissionsWrapper'
import Contracts from './Contracts'
import ContractsSchema from './ContractSchema'
import ContractSchemasPending from './ContractSchemasPending'
import ContractsPending from './ContractsPending'
import Reports from './Reports'

type ContractRow = IContract & { csi: CSimilarity, counterpartySignatory: any }

export default function ContractsHub() {
    const routeParams = useAppSelector(state => state.session.routeParams)
    const defaultTab = routeParams?.tab ?? 'contracts'

    const [openSchemaDialog, setOpenSchemaDialog] = useState(false)
    const [filteredContracts, setFilteredContracts] = useState<ContractRow[]>([])
    const [selectedTab, setSelectedTab] = useState<string>(defaultTab)
    const [openModalCreateReport, setOpenModalCreateReport] = useState(false)
    const [isPending, setIsPending] = useState<boolean>(false)
    const [isSchemasPending, setIsSchemasPending] = useState<boolean>(false)

    const renderContractButtons = () => {
        const dispatch = useAppDispatch()
        const [open, setOpen] = useState(false)

        const onFinish = (res: IUploadedResult) => {
            dispatch(changeRoute({ route: 'contract-editor', params: { contractUUID: res.contractUUID, uploadedFiles: res.uploadedfiles } }))
        }
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            setIsPending((event.target.checked))
        }

        return (
            <Stack direction='row' spacing={1}>
                <PermissionsWrapper permission='contracts.create'>
                    <FormControlLabel control={<Switch checked={isPending} onChange={handleChange} />} label='Your pending items' labelPlacement='start' />
                </PermissionsWrapper>
                <PermissionsWrapper permission='contracts-analysis.view'>
                    <Button
                        disabled={isPending}
                        variant='text'
                        startIcon={<Troubleshoot />}
                        onClick={() => {
                            dispatch(
                                changeRoute({
                                    route: 'contracts-analysis',
                                    params: { contracts: filteredContracts }
                                })
                            )
                        }}>Comparison
                    </Button>
                </PermissionsWrapper>
                <PermissionsWrapper permission='contracts.create'>
                    <Button
                        variant='contained'
                        startIcon={<AddRounded />}
                        onClick={() => setOpen(true)}>
                        New contract
                    </Button>
                </PermissionsWrapper>
                <Modal open={open} onClose={() => setOpen(false)}>
                    <div>
                        <NewContractModal onFinish={onFinish} onClose={() => setOpen(false)} />
                    </div>
                </Modal>
            </Stack>
        )
    }

    const renderNewSchema = () => {
        return (
            <Stack direction='row' spacing={1}>
                <PermissionsWrapper permission='contract-schema.create'>
                    <FormControlLabel
                        control={<Switch checked={isSchemasPending} onChange={event => setIsSchemasPending(event.target.checked)} />}
                        label='Your pending items'
                        labelPlacement='start'
                    />
                </PermissionsWrapper>
                <PermissionsWrapper permission='contract-schema.create'>
                    <Button
                        onClick={() => setOpenSchemaDialog(true)}
                        variant='contained'
                        startIcon={<AddRounded />}>
                        New Schema
                    </Button>
                </PermissionsWrapper>
            </Stack>
        )
    }

    const renderNewReport = () => {
        return (
            <Stack direction='row' spacing={1}>
                <Button
                    onClick={() => setOpenModalCreateReport(true)}
                    variant='contained'
                    startIcon={<AddRounded />}>
                    New Report
                </Button>
                <Modal open={openModalCreateReport} onClose={() => setOpenModalCreateReport(false)} style={{ overflow: 'auto' }}>
                    <>
                        <ModalCreateReport onClose={() => setOpenModalCreateReport(false)} />
                    </>
                </Modal>
            </Stack>
        )
    }

    const tabs: ITabItem[] = [
        {
            id: 'contracts',
            name: 'Contracts',
            renderPanel: () => isPending ? <ContractsPending /> : <Contracts setFilteredContracts={setFilteredContracts} />,
            rightRender: renderContractButtons()
        },
        {
            id: 'schemas',
            name: 'Schemas',
            renderPanel: () => isSchemasPending ? <ContractSchemasPending /> : <ContractsSchema openSchemaDialog={openSchemaDialog} onCloseDialog={() => setOpenSchemaDialog(false)} />,
            rightRender: renderNewSchema()
        },
        {
            id: 'reports',
            name: 'Report Templates',
            renderPanel: () => <Reports />,
            rightRender: renderNewReport()
        }
    ]

    return (
        <Tile.Container>
            <Stack direction='row'>
                <Stack spacing={1}>
                    <Typography variant='h5'>
                        <strong>Contracts Repository</strong>
                    </Typography>
                    <Typography variant='body1'>
                        Manage and track your contracts here
                    </Typography>
                </Stack>
            </Stack>
            <Tile.Container>
                <TabsHeader
                    tabs={tabs}
                    defaultSelectedTab={selectedTab}
                    onTabChange={(tabId: string) => setSelectedTab(tabId)}
                />
            </Tile.Container>
        </Tile.Container>
    )
}
